export type MediaRef = {
  tag: string;
  src?: string;
  alt?: string;
  widthCells: number;
  heightLines: number;
  attrs?: Record<string, string>;
};

export type Style = {
  text: string;
  color?: string;
  bg?: string;
  bold?: boolean;
  dim?: boolean;
  inverted?: boolean;
  blink?: boolean;
  interactionId?: string;
  onClick?: () => void;
  noSelect?: boolean;
  cell?: number;
  media?: MediaRef;
};

export type Segment = string | Style;
export type Line = Segment[];
export type Block = Line[];
export type LayoutFn = (width: number) => Block;

export type ThemeMode = "light" | "dark";
export type ThemePreference = ThemeMode | "system";

export type ThemePalette = {
  black: string;
  red: string;
  green: string;
  yellow: string;
  blue: string;
  magenta: string;
  cyan: string;
  white: string;
  brightBlack: string;
  brightWhite: string;
};

export type ThemeSemantic = {
  frameBg: string;
  frameFg: string;
  frameMuted: string;
  border: string;
  borderStrong: string;
  inverseFg: string;
  inverseBg: string;
  info: string;
  success: string;
  warn: string;
  danger: string;
};

export type ThemeSyntax = {
  default: string;
  keyword: string;
  string: string;
  number: string;
  comment: string;
  punctuation: string;
  function: string;
  type: string;
};

export type ThemeMarkdown = {
  headingMarker: string;
  headingBoldMaxLevel: number;
  headings: {
    h1: string;
    h2: string;
    h3: string;
    h4: string;
    h5: string;
    h6: string;
  };
  paragraph: string;
  unorderedBullet: string;
  orderedMarker: string;
  tableHeader: string;
  tableBorder: string;
  quoteMarker: string;
  quoteText: string;
  separator: string;
  codeText: string;
  codeEmpty: string;
  codeBorder: string;
};

export type ThemeComponents = {
  tabs: {
    activeFg: string;
    activeBg: string;
    inactiveFg: string;
    separator: string;
  };
  statusbar: {
    fg: string;
    bg: string;
  };
  badge: {
    fg: string;
    bg: string;
  };
  button: {
    fg: string;
    bg: string;
  };
};

export type Theme = {
  id: string;
  label: string;
  version: number;
  mode: ThemeMode;
  palette: ThemePalette;
  semantic: ThemeSemantic;
  syntax: ThemeSyntax;
  markdown: ThemeMarkdown;
  components: ThemeComponents;

  // Convenience aliases for the most common color paths.
  black: string;
  red: string;
  green: string;
  yellow: string;
  blue: string;
  magenta: string;
  cyan: string;
  white: string;
  brightBlack: string;
  brightWhite: string;
  border: string;
  bg: string;
};

export function normalizedWidth(width: number): number {
  if (!Number.isFinite(width)) return 0;
  return Math.max(0, Math.floor(width));
}
